/**
 * Écran « Réglages » : du brouillon saisi (chaînes, virgule décimale française) à la grille envoyée à
 * la base — logique PURE, testée. La base vérifie de toute façon la forme (`tarifs_operes_forme_chk`,
 * contraintes de colonnes) ; `verifierGrille` (partagée avec le calcul) le dit AVANT l'envoi, champ par champ.
 */
import { calculerPrixOpere, verifierGrille, VEHICULES, type GrilleOperee, type TypeVehicule } from './prixOpere'
import type { TarifsOperes } from './types'

export interface Brouillon {
  tranches: { jusqu: string; prix: string }[]
  coefs: Record<TypeVehicule, string>
  seuil: string
  kmSup: string
  kmSupOption: string
  optionMensuel: string
  supplement: string
  arrondi: string
  /** Prix à 0 km (0043). */
  minimum: string
  /** Majoration de tension (0043) : pourcentages saisis en % (« 20 » pour 20 %). */
  tension: { active: boolean; rayon: string; seuil: string; pente: string; plafond: string; partLivreur: string }
  /** Frais mobile money, en POURCENT (« 1,5 » pour 1,5 %) : plus lisible que la fraction 0,015 de la base. */
  mm: { encaissementWave: string; encaissementOm: string; decaissementWave: string; decaissementOm: string }
}

const texte = (n: number): string => String(n).replace('.', ',')

/** `1 500`, `1,5`, `1.5` → nombre ; vide ou illisible → NaN. */
export function nombreFR(s: string): number {
  const t = s.replace(/[\s  ]/g, '').replace(',', '.')
  return t === '' || !/^-?\d*\.?\d+$/.test(t) ? Number.NaN : Number(t)
}

/** Fraction de la base (0,015) → pourcentage affiché (1,5). Arrondi à 4 décimales : pas de 0,15000000000000002. */
export const pourcentDepuisFraction = (f: number): string => texte(Math.round(f * 100 * 10000) / 10000)

export function brouillonDepuis(t: TarifsOperes): Brouillon {
  const coefs = {} as Record<TypeVehicule, string>
  for (const v of VEHICULES) coefs[v] = texte(t.coefficients[v])
  return {
    tranches: t.tranches.map((x) => ({ jusqu: texte(x.jusqu_a_km), prix: texte(x.prix_fcfa) })),
    coefs,
    seuil: texte(t.seuil_longue_distance_km),
    kmSup: texte(t.prix_km_sup_fcfa),
    kmSupOption: texte(t.prix_km_sup_option_fcfa),
    optionMensuel: texte(t.option_longue_distance_mensuel_fcfa),
    supplement: texte(t.supplement_hors_creneau_fcfa),
    arrondi: texte(t.arrondi_fcfa),
    minimum: texte(t.prix_minimum_fcfa),
    tension: {
      active: t.tension_active,
      rayon: texte(t.tension_rayon_km),
      seuil: texte(t.tension_seuil),
      pente: texte(t.tension_pente_pct),
      plafond: texte(t.tension_plafond_pct),
      partLivreur: texte(t.tension_part_livreur_pct),
    },
    mm: {
      encaissementWave: pourcentDepuisFraction(t.frais_mm_encaissement_wave),
      encaissementOm: pourcentDepuisFraction(t.frais_mm_encaissement_om),
      decaissementWave: pourcentDepuisFraction(t.frais_mm_decaissement_wave),
      decaissementOm: pourcentDepuisFraction(t.frais_mm_decaissement_om),
    },
  }
}

export type TarifsAEnvoyer = Omit<TarifsOperes, 'grille_provisoire' | 'updated_at' | 'bareme_progressif_en_base'>

export type LectureBrouillon = { ok: true; tarifs: TarifsAEnvoyer; grille: GrilleOperee } | { ok: false; problemes: string[] }

export function lireBrouillon(b: Brouillon): LectureBrouillon {
  const problemes: string[] = []
  const num = (s: string, nom: string): number => {
    const n = nombreFR(s)
    if (Number.isNaN(n)) problemes.push(`${nom} : saisissez un nombre.`)
    return n
  }
  const tranches = b.tranches.map((t, i) => ({
    jusqu_a_km: num(t.jusqu, `Tranche ${i + 1}, distance`),
    prix_fcfa: num(t.prix, `Tranche ${i + 1}, prix`),
  }))
  const coefficients = {} as Record<TypeVehicule, number>
  for (const v of VEHICULES) coefficients[v] = num(b.coefs[v], `Coefficient ${v}`)
  const grille: GrilleOperee = {
    tranches,
    coefficients,
    seuil_longue_distance_km: num(b.seuil, 'Seuil de longue distance'),
    prix_km_sup_fcfa: num(b.kmSup, 'Prix du km supplémentaire'),
    prix_km_sup_option_fcfa: num(b.kmSupOption, 'Prix du km supplémentaire avec option'),
    supplement_hors_creneau_fcfa: num(b.supplement, 'Supplément hors créneau'),
    arrondi_fcfa: num(b.arrondi, 'Arrondi'),
    prix_minimum_fcfa: num(b.minimum, 'Prix minimum'),
    tension_active: b.tension.active,
    tension_rayon_km: num(b.tension.rayon, 'Rayon de la zone de tension'),
    tension_seuil: num(b.tension.seuil, 'Seuil de tension'),
    tension_pente_pct: num(b.tension.pente, 'Pente de la majoration'),
    tension_plafond_pct: num(b.tension.plafond, 'Plafond de la majoration'),
    tension_part_livreur_pct: num(b.tension.partLivreur, 'Part du livreur'),
  }
  const optionMensuel = num(b.optionMensuel, 'Abonnement mensuel de l’option')
  if (!Number.isInteger(optionMensuel) || optionMensuel < 0) problemes.push('Abonnement mensuel de l’option : un nombre entier de francs, positif ou nul.')

  const pct = (s: string, nom: string): number => {
    const n = num(s, nom)
    if (!Number.isNaN(n) && (n < 0 || n > 10)) problemes.push(`${nom} : entre 0 et 10 %.`)
    // Fraction à 4 décimales, comme numeric(6,4).
    return Math.round(n * 100) / 10000
  }
  const mm = {
    frais_mm_encaissement_wave: pct(b.mm.encaissementWave, 'Frais d’encaissement Wave'),
    frais_mm_encaissement_om: pct(b.mm.encaissementOm, 'Frais d’encaissement Orange Money'),
    frais_mm_decaissement_wave: pct(b.mm.decaissementWave, 'Frais de décaissement Wave'),
    frais_mm_decaissement_om: pct(b.mm.decaissementOm, 'Frais de décaissement Orange Money'),
  }

  // La forme complète n'est vérifiable que si tout est numérique.
  if (problemes.length === 0) problemes.push(...verifierGrille(grille))
  if (problemes.length > 0) return { ok: false, problemes }
  return { ok: true, grille, tarifs: { ...grille, option_longue_distance_mensuel_fcfa: optionMensuel, ...mm } }
}

/* ------------------------------------------------------------------ aperçu du barème (0043) */

/** Distances de l'aperçu montré AVANT d'enregistrer la grille (décision du 12/10/2026). */
export const DISTANCES_APERCU: readonly number[] = [0.1, 0.5, 1, 2, 3, 5, 8, 12, 15, 20]

export interface LigneApercu {
  km: number
  /** Prix de la course (sans supplément ni majoration) avec la grille du brouillon. */
  prix: number
  /** Même distance avec la grille ENREGISTRÉE (null si elle est illisible) : pour voir l'effet de la modification. */
  avant: number | null
}

/** Le prix aux distances de l'aperçu, pour un véhicule — brouillon et grille enregistrée côte à côte. */
export function apercuBareme(grille: GrilleOperee, vehicule: TypeVehicule, enregistree: GrilleOperee | null): LigneApercu[] {
  return DISTANCES_APERCU.map((km) => ({
    km,
    prix: calculerPrixOpere(grille, { distance_km: km, vehicule }).prix_course,
    avant: enregistree ? calculerPrixOpere(enregistree, { distance_km: km, vehicule }).prix_course : null,
  }))
}
